import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, API } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { ShareResults } from "@/components/ShareResults";
import { DownloadSimple, Trophy, Scales, Check, ArrowsSplit, ShieldCheck } from "@phosphor-icons/react";
import GuideLink from "@/components/GuideLink";

const CHART_MARGIN = { left: 0, right: 40 };
const Y_TICK = { fontSize: 12, fill: "#52525B" };
const TOOLTIP_CURSOR = { fill: "#f4f4f5" };
const TOOLTIP_STYLE = { borderRadius: 6, border: "1px solid #e4e4e7", fontSize: 12 };
const BAR_LABEL = { position: "right", fontSize: 11, fill: "#09090b", formatter: (v) => v };

export default function ResultsTab({ electionId }) {
  const [data, setData] = useState(null);
  const nav = useNavigate();

  const load = useCallback(() => {
    api.get(`/elections/${electionId}/results`).then(({ data }) => setData(data)).catch(() => setData({ results: [], turnout: { total: 0, voted: 0, pct: 0 } }));
  }, [electionId]);
  useEffect(() => { load(); }, [load]);

  const resolveTie = async (positionId, candidateId) => {
    try { await api.post(`/positions/${positionId}/winner`, { candidate_id: candidateId }); toast.success("Winner declared"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const startRunoff = async (positionId) => {
    try {
      const { data } = await api.post(`/positions/${positionId}/runoff`);
      toast.success("Runoff election created");
      nav(`/app/elections/${data.election_id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/elections/${electionId}/results/export`, { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `results_${electionId}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Results exported");
    } catch { toast.error("Export failed"); }
  };

  const exportPdf = async () => {
    try {
      const res = await fetch(`${API}/elections/${electionId}/results/pdf`, { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `results_${electionId}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("PDF failed"); }
  };

  const exportIntegrity = async () => {
    try {
      const res = await fetch(`${API}/elections/${electionId}/integrity-report`, { credentials: "include" });
      if (!res.ok) { toast.error("Integrity report failed"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `integrity_${electionId}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Integrity report downloaded");
    } catch { toast.error("Integrity report failed"); }
  };

  if (!data) return <p className="text-zinc-400 font-mono text-sm">loading…</p>;

  const integ = data.integrity || {};
  const orphaned = integ.orphaned_claims || 0;
  const flagged = integ.flagged_votes || 0;
  const showIntegrity = orphaned > 0 || flagged > 0 || (integ.credential_mints || 0) > 0 || (integ.contact_edits || 0) > 0;

  return (
    <div className="space-y-8">
      {showIntegrity && (
        <div data-testid="integrity-panel" className={`border px-5 py-4 rounded-md ${orphaned > 0 || flagged > 0 ? "border-amber-300 bg-amber-50" : "border-zinc-200 bg-zinc-50"}`}>
          <div className="flex items-start gap-3">
            <Scales size={20} weight="bold" className={`mt-0.5 shrink-0 ${orphaned > 0 || flagged > 0 ? "text-amber-700" : "text-zinc-500"}`} />
            <div className="text-sm">
              <p className={`font-semibold ${orphaned > 0 || flagged > 0 ? "text-amber-900" : "text-zinc-700"}`}>Integrity summary</p>
              <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 font-mono text-xs">
                <span data-testid="integrity-flagged">Flagged votes: <strong>{flagged}</strong></span>
                <span data-testid="integrity-orphaned">Orphaned claims: <strong>{orphaned}</strong></span>
                <span data-testid="integrity-mints">Admin-minted credentials: <strong>{integ.credential_mints || 0}</strong></span>
                <span data-testid="integrity-edits">Contact edits: <strong>{integ.contact_edits || 0}</strong></span>
              </div>
              {flagged > 0 && <p className="mt-2 text-amber-800">{flagged} vote{flagged === 1 ? " was" : "s were"} cast after an admin minted a link/PIN or edited contact details within 30 minutes — review the flagged voters in the Voters tab.</p>}
              {orphaned > 0 && <p className="mt-1 text-amber-800">{orphaned} orphaned claim{orphaned === 1 ? "" : "s"} (voter marked voted but no ballot recorded). No ballot was double-counted; open elections auto-restore these voters.</p>}
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="bg-white border border-zinc-200 px-6 py-4 inline-flex items-center gap-6">
          <div>
            <div className="text-3xl font-display font-bold tracking-tight">{data.turnout.pct}%</div>
            <div className="text-xs uppercase tracking-widest text-zinc-400">Turnout</div>
          </div>
          <div className="h-10 w-px bg-zinc-200" />
          <div className="font-mono text-sm text-zinc-500">{data.turnout.voted} / {data.turnout.total} voted</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ShareResults
            imagePath={`/elections/${electionId}/results/image`}
            shareUrl={`${window.location.origin}/results/${electionId}`}
            title={data.election?.title || "Election"}
            authed
          />
          <button data-testid="export-results-btn" onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2.5 border border-zinc-300 rounded-md text-sm font-medium hover:border-black transition-colors">
            <DownloadSimple size={16} /> Export CSV
          </button>
          <button data-testid="export-pdf-btn" onClick={exportPdf} className="inline-flex items-center gap-2 px-4 py-2.5 border border-zinc-300 rounded-md text-sm font-medium hover:border-black transition-colors">
            <DownloadSimple size={16} /> PDF report
          </button>
          <button data-testid="export-integrity-btn" onClick={exportIntegrity} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold text-white transition-colors" style={{ background: "#1F6FE5" }}>
            <ShieldCheck size={16} weight="fill" /> Integrity report
          </button>
          <GuideLink section="results" />
        </div>
      </div>

      {data.results.length === 0 || data.results.every((r) => r.total_ballots === 0) ? (
        <div className="border border-dashed border-zinc-300 bg-white p-14 text-center text-zinc-500">No votes have been cast yet.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
          {data.results.map((r) => {
            const isRanked = r.ballot_type === "ranked";
            const chartData = r.tally.map((c) => ({ name: c.name, value: isRanked ? c.points : c.votes, pct: c.pct }));
            const max = Math.max(1, ...chartData.map((d) => d.value));
            const closed = data.election?.status === "closed";
            const pendingTie = r.is_tie && !r.resolved;
            return (
              <div key={r.position_id} data-testid={`result-${r.position_id}`} className="bg-white border border-zinc-200 p-6">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-display font-bold text-lg tracking-tight flex items-center gap-2">
                    {r.title}
                    {pendingTie && <span data-testid={`tie-badge-${r.position_id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm"><Scales size={11} weight="bold" /> Tie</span>}
                  </h3>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-400">{isRanked ? "Ranked · Borda" : "Single choice"}</span>
                </div>
                <p className="text-xs font-mono text-zinc-400 mb-4">{r.total_ballots} ballots</p>
                <ResponsiveContainer width="100%" height={Math.max(120, r.tally.length * 52)}>
                  <BarChart layout="vertical" data={chartData} margin={CHART_MARGIN}>
                    <XAxis type="number" hide domain={[0, max]} />
                    <YAxis type="category" dataKey="name" width={110} tick={Y_TICK} axisLine={false} tickLine={false} />
                    <Tooltip cursor={TOOLTIP_CURSOR} formatter={(v) => [isRanked ? `${v} pts` : `${v} votes`, ""]} contentStyle={TOOLTIP_STYLE} />
                    <Bar dataKey="value" radius={[0, 3, 3, 0]} barSize={22} label={BAR_LABEL}>
                      {chartData.map((_, i) => <Cell key={r.tally[i].id} fill={r.tally[i].id === r.winner_id ? "#1F6FE5" : "#09090b"} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2.5">
                  {(() => {
                    const maxV = Math.max(...r.tally.map((c) => (isRanked ? c.points : c.votes)), 1);
                    return r.tally.map((c) => {
                      const isWin = c.id === r.winner_id;
                      const w = isRanked ? (c.points / maxV) * 100 : c.pct;
                      return (
                        <div key={c.id} data-testid={`tally-${c.id}`} className={`rounded-xl p-3 transition-colors ${isWin ? "bg-[#F0F5FE] border-l-4 border-[#1F6FE5] pl-4" : ""}`}>
                          <div className="flex items-center justify-between mb-1.5 gap-3">
                            <span className="flex items-center gap-2 text-sm font-medium min-w-0">
                              {isWin && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1F6FE5] text-white text-[10px] font-bold uppercase tracking-wide shrink-0">
                                  <Trophy size={11} weight="fill" /> Winner
                                </span>
                              )}
                              <span className="truncate">{c.name}</span>
                            </span>
                            <span className="font-mono text-sm text-zinc-600 shrink-0">{isRanked ? `${c.points} pts` : `${c.votes} · ${c.pct}%`}</span>
                          </div>
                          <div className="h-2 w-full bg-zinc-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(w, 2)}%`, background: isWin ? "#1F6FE5" : "#9ca3af" }} />
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                {closed && pendingTie && (
                  <div data-testid={`tie-resolver-${r.position_id}`} className="mt-4 border-t border-zinc-100 pt-4">
                    <p className="text-xs text-amber-700 mb-2 font-medium">Tie between {r.tied_ids.length} candidates — declare the official winner:</p>
                    <div className="flex flex-wrap gap-2">
                      {r.tally.filter((c) => r.tied_ids.includes(c.id)).map((c) => (
                        <button key={c.id} data-testid={`resolve-${r.position_id}-${c.id}`} onClick={() => resolveTie(r.position_id, c.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-300 rounded-md text-sm font-medium hover:border-[#1F6FE5] hover:text-[#1F6FE5] transition-colors">
                          <Check size={14} weight="bold" /> {c.name}
                        </button>
                      ))}
                      <button data-testid={`runoff-${r.position_id}`} onClick={() => startRunoff(r.position_id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#14B8A6] text-white rounded-md text-sm font-semibold hover:bg-[#0e9488] transition-colors active:scale-95">
                        <ArrowsSplit size={14} weight="bold" /> Start runoff
                      </button>
                    </div>
                  </div>
                )}
                {closed && r.resolved && (
                  <p className="mt-3 text-xs text-emerald-600 font-medium">✓ Winner declared by an administrator</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
